import { createSlice, createAsyncThunk, PayloadAction, createAction } from '@reduxjs/toolkit';
import axios from '../../../config/axios';
import Toast from 'src/shared/toast/Toast';
import { t } from "i18next";
import {
    WhatsAppTemplate,
    TemplatesListFilters,
    TemplatesListResponse,
    CreateTemplateRequest,
    UpdateTemplateRequest,
    WhatsappTemplatesState,
    RequiredPlaceholdersEnum,
} from 'src/utils/interfaces/whatsapp/WhatsappTemplatesTypes';
import { WHATSAPP_TEMPLATES_PAGE_LIMIT } from 'src/utils/constants/constants';
import { ResponseResult } from 'src/utils/interfaces/http/ResponseResult';
import { TemplateSourceEnum } from 'src/utils/interfaces/whatsapp/WhatsappInboxTypes';

const appKey = 'whatsappTemplates';
const apiModuleUrl = 'pa/whatsapp/templates';

// Async Thunks
export const fetchTemplates = createAsyncThunk(`${appKey}/fetchTemplates`, async (filters: TemplatesListFilters): Promise<TemplatesListResponse> => {
    // const newFilters = { ...filters }; delete newFilters.source;
    const response = await axios.get<ResponseResult<TemplatesListResponse>>(`${apiModuleUrl}`, { params: filters });
    return response.data.data;
});

export const fetchTemplateById = createAsyncThunk(`${appKey}/fetchTemplateById`, async (id: string): Promise<WhatsAppTemplate> => {
    const response = await axios.get<ResponseResult<WhatsAppTemplate>>(`${apiModuleUrl}/${id}`);
    return response.data.data;
});

export const createTemplate = createAsyncThunk(`${appKey}/createTemplate`, async (data: CreateTemplateRequest): Promise<WhatsAppTemplate> => {
    const response = await axios.post<ResponseResult<WhatsAppTemplate>>(`${apiModuleUrl}`, data);
    return response.data.data;
});

export const updateTemplate = createAsyncThunk(`${appKey}/updateTemplate`, async ({ id, data }: { id: string; data: UpdateTemplateRequest }): Promise<WhatsAppTemplate> => {
    const response = await axios.put<ResponseResult<WhatsAppTemplate>>(`${apiModuleUrl}/${id}`, data);
    return response.data.data;
});


export const deleteTemplate = createAsyncThunk('whatsappTemplates/deleteTemplate', async (id: string) => {
    const response = await axios.delete<ResponseResult<void>>(`${apiModuleUrl}/${id}`);
    return id;
});


export const setShowDeleteWhatsappTemplateModal = createAction<boolean>(`${appKey}/setShowDeleteWhatsappTemplateModal`);
export const setShowSubmitWhatsappTemplateModal = createAction<boolean>(`${appKey}/setShowSubmitWhatsappTemplateModal`);
export const setCurrentWhatsappTemplate = createAction<WhatsAppTemplate>(`${appKey}/setCurrentWhatsappTemplate`);
export const setShowSubmitForApprovalModal = createAction<boolean>(`${appKey}/setShowSubmitForApprovalModal`);


const initialState: WhatsappTemplatesState = {
    templates: [],
    loading: false,
    error: null,
    filters: {
        page: 1,
        limit: WHATSAPP_TEMPLATES_PAGE_LIMIT,
    },

    currentTemplate: null,
    showDeleteWhatsappTemplateModal: false,
    showSubmitWhatsappTemplateModal: false,
};


// Slice
const whatsappTemplatesSlice = createSlice({
    name: appKey,
    initialState,
    reducers: {
        setFilters: (state, action: PayloadAction<TemplatesListFilters>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearCurrentTemplate: (state) => {
            state.currentTemplate = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch Templates
        builder.addCase(fetchTemplates.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchTemplates.fulfilled, (state, action) => {
            state.loading = false;
            state.templates = action.payload.templates;

        });
        builder.addCase(fetchTemplates.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to fetch templates';
            Toast.Error({ content: action.error?.message })
        });

        // Fetch Template By ID
        builder.addCase(fetchTemplateById.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchTemplateById.fulfilled, (state, action) => {
            state.loading = false;
            state.currentTemplate = action.payload;
            // state.currentTemplate = { ...action.payload, status: TemplateStatus.REJECTED }; // for testing
        });
        builder.addCase(fetchTemplateById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to fetch template';
            Toast.Error({ content: action.error?.message })
        });

        // Create Template
        builder.addCase(createTemplate.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createTemplate.fulfilled, (state, action) => {
            // state.loading = false;
            state.templates.push(action.payload);
            Toast.Success({ content: t('template_submitted_successfully') })
        });
        builder.addCase(createTemplate.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to create template';
            Toast.Error({ content: action.error?.message })
        });

        // Update Template
        builder.addCase(updateTemplate.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateTemplate.fulfilled, (state, action) => {
            state.loading = false;
            const index = state.templates.findIndex(t => t.id === action.payload.id);
            if (index !== -1) {
                state.templates[index] = action.payload;
            }
            if (state.currentTemplate?.id === action.payload.id) {
                state.currentTemplate = action.payload;
            }
            Toast.Success({ content: t('template_submitted_successfully') })

        });
        builder.addCase(updateTemplate.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to update template';
            Toast.Error({ content: action.error?.message })
        });

        // Delete Template
        builder.addCase(deleteTemplate.fulfilled, (state, action) => {
            state.templates = state.templates.filter(t => t.id !== action.payload);
            Toast.Success({ content: t('template_deleted') });
        });

        builder.addCase(deleteTemplate.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to delete template';
            Toast.Error({ content: action.error?.message })
        });

        // Set Show Delete Whatsapp Template Modal
        builder.addCase(setShowDeleteWhatsappTemplateModal, (state, action) => {
            state.showDeleteWhatsappTemplateModal = action.payload;
        });

        // Set Show Submit Whatsapp Template Modal
        builder.addCase(setShowSubmitWhatsappTemplateModal, (state, action) => {
            state.showSubmitWhatsappTemplateModal = action.payload;
        });

        // Set Current Whatsapp Template
        builder.addCase(setCurrentWhatsappTemplate, (state, action) => {
            state.currentTemplate = action.payload;
        });
    },
});

export const { setFilters, clearCurrentTemplate, clearError } = whatsappTemplatesSlice.actions;
export default whatsappTemplatesSlice.reducer;
